import { useEffect, useMemo, useState } from "react";
import { auth, db } from "../Context/firebaseConfig";
import {
    doc,
    getDoc,
    serverTimestamp,
    setDoc,
} from "firebase/firestore";
import { format } from "date-fns";
import TheNav from "../Components/navbar";
import sndThumbnail from "../images/anteos1.png";
import SEO from "../Components/SEO";
import {
    AlertCircle,
    CheckCircle2,
    Coins,
    Gift,
    LayoutGrid,
    Loader2,
    Lock,
    Sparkles,
    Ticket,
    Wallet,
} from "lucide-react";
import {
    queueTransactionalEmail,
    transactionalEmails,
} from "../utils/transactionalEmail";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const rewardOptions = [
  { type: "money", label: "$5 Cash", image: sndThumbnail, cost: 500 },
  {
    type: "money",
    label: "$10 Cash",
    image: "https://img.youtube.com/vi/DIGvPWwbln8/maxresdefault.jpg",
    cost: 1200,
  },
  {
    type: "money",
    label: "$25 Cash",
    image:
      "https://i.ytimg.com/vi/4EKTICn31_U/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLD3wg6dS6Ei0v5cH8ZgpZXfuuZKfA",
    cost: 2200,
  },
  {
    type: "money",
    label: "$50 Cash",
    image: "https://pbs.twimg.com/media/GRSTd5kaMAAsjpe.jpg",
    cost: 4200,
  },
  {
    type: "coupon",
    label: "$2 Coupon",
    image:
      "https://i.ytimg.com/vi/pkQxv0wYHxw/maxresdefault.jpg?sqp=-oaymwEmCIAKENAF8quKqQMa8AEB-AHUBoAC4AOKAgwIABABGGUgZShlMA8=&rs=AOn4CLBEJnyu43XBjdXZ51IGmyOdoaXqvw",
    cost: 200,
  },
  {
    type: "coupon",
    label: "$5 Coupon",
    image: "https://i.ytimg.com/vi/Ep0Zo-Jtsi4/maxresdefault.jpg",
    cost: 370,
  },
  {
    type: "coupon",
    label: "$10 Coupon",
    image:
      "https://esportsinsider.com/wp-content/uploads/2025/04/Kyedae-steps-away-from-streaming.jpg",
    cost: 655,
  },
  {
    type: "coupon",
    label: "$20 Coupon",
    image: "https://pbs.twimg.com/media/FGXYC0yVEAIuk2z.jpg:large",
    cost: 1500,
  },
];

const NEXT_REWARD_THRESHOLD = 500;

export default function Redeem() {
  const [points, setPoints] = useState(0);
  const [role, setRole] = useState("");
  const [alert, setAlert] = useState(null);
  const [uid, setUid] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");
  const today = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      try {
        if (!user) {
          setUid(null);
          setLoading(false);
          return;
        }

        setUid(user.uid);
        const userRef = doc(db, "Users", user.uid);
        const snap = await getDoc(userRef);

        if (snap.exists()) {
          const data = snap.data();
          setPoints(data.profileInfo?.points || 0);
          setRole(data.profileInfo?.role || "");
          setUsername(data.profileInfo?.username || "");
          setEmail(data.profileInfo?.email || user.email || "");
        }
      } catch (error) {
        console.error("Unable to load rewards profile:", error);
        setAlert({
          type: "error",
          message: "Could not load your rewards profile. Try refreshing.",
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const isCreator = role?.toLowerCase() === "creator";

  const availableRewards = useMemo(() => {
    let rewards = isCreator
      ? rewardOptions
      : rewardOptions.filter((reward) => reward.type === "money");

    if (isCreator && filter !== "all") {
      rewards = rewards.filter((reward) => reward.type === filter);
    }

    return rewards;
  }, [filter, isCreator]);

  const handleRewardRequest = async (type, cost, label) => {
    if (!uid) {
      setAlert({ type: "error", message: "Sign in to redeem rewards." });
      return;
    }

    if (points < cost) {
      setAlert({ type: "error", message: "You don't have enough points." });
      return;
    }

    setAlert(null);
    const rewardRef = doc(db, `RewardRequests/${uid}/requests`, today);

    try {
      setSubmitting(true);
      const rewardSnap = await getDoc(rewardRef);

      if (rewardSnap.exists()) {
        setAlert({
          type: "error",
          message: "You've already made a request today.",
        });
        return;
      }

      await setDoc(rewardRef, {
        type,
        label,
        date: today,
        uid,
        username,
        points: cost,
        status: "pending",
        timestamp: serverTimestamp(),
      });

      if (email) {
        try {
          await queueTransactionalEmail({
            to: email,
            email: transactionalEmails.rewardRequest({
              username: username || "Kre8 member",
              rewardLabel: label,
            }),
          });
        } catch (mailError) {
          console.warn("Reward request mail failed:", mailError);
        }
      }

      setAlert({
        type: "success",
        message: `Your ${type} reward request was submitted!`,
      });
    } catch (error) {
      console.error("Reward request failed:", error);
      setAlert({
        type: "error",
        message: "Something went wrong submitting your request.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.min(points, NEXT_REWARD_THRESHOLD);
  const progressPercent = Math.min(
    (progress / NEXT_REWARD_THRESHOLD) * 100,
    100
  );
  const pointsToNext = Math.max(NEXT_REWARD_THRESHOLD - points, 0);

  return (
    <>
      <SEO
        title="Rewards | Kre8"
        description="Private Kre8 rewards and points workspace."
        canonicalPath="/rewards"
        noindex
      />

      <TheNav />
      <main className="relative min-h-screen overflow-hidden bg-[#07060d] text-white">
        <div
          className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full opacity-25 blur-[140px]"
          style={{
            background: `radial-gradient(circle, ${PURPLE}, transparent 60%)`,
          }}
        />
        <div
          className="pointer-events-none absolute right-0 top-72 h-[380px] w-[520px] rounded-full opacity-20 blur-[120px]"
          style={{
            background: `radial-gradient(circle, ${MINT}, transparent 60%)`,
          }}
        />

        <div className="relative mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
          <GlassPanel glow="purple" className="mb-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  Rewards
                </p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                  Redeem Rewards
                </h1>
                <p className="mt-2 max-w-xl text-sm text-white/60">
                  Use your Kre8 points for cash rewards and creator coupons.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {role && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"
                    style={{
                      color: isCreator ? PURPLE : MINT,
                      background: isCreator
                        ? "rgba(168,85,247,0.12)"
                        : "rgba(16,185,129,0.12)",
                      borderColor: isCreator
                        ? "rgba(168,85,247,0.3)"
                        : "rgba(16,185,129,0.3)",
                    }}
                  >
                    <Sparkles size={11} />
                    {role}
                  </span>
                )}
                {username && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-white/70">
                    @{username}
                  </span>
                )}
              </div>
            </div>
          </GlassPanel>

          {!loading && !uid ? (
            <Alert type="error" icon={Lock}>
              Sign in to view rewards.
            </Alert>
          ) : (
            <>
              <GlassPanel glow="mint" className="mb-5">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40">
                      Balance
                    </p>
                    <div className="mt-1 flex items-end gap-2">
                      <span
                        className="text-5xl font-bold leading-none tracking-tight"
                        style={{ color: MINT }}
                      >
                        {loading ? "-" : points}
                      </span>
                      <span className="pb-1 text-sm text-white/50">
                        points available
                      </span>
                    </div>
                  </div>
                  <div
                    className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/5"
                    style={{
                      color: MINT,
                      boxShadow: `0 0 30px -8px ${MINT}66`,
                    }}
                  >
                    <Coins size={22} />
                  </div>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between text-xs text-white/50">
                    <span>Progress to next $5 cash reward</span>
                    <span className="font-mono text-white/70">
                      {progress}/{NEXT_REWARD_THRESHOLD}
                    </span>
                  </div>
                  <ProgressBar value={progressPercent} />
                  <p className="mt-2 text-xs text-white/50">
                    {pointsToNext} points away from your next $5 cash reward.
                  </p>
                </div>

                <p className="mt-4 rounded-2xl border border-white/5 bg-white/[0.02] p-3 text-xs leading-relaxed text-white/50">
                  When you have enough points, request a redemption. We'll
                  contact you within 2 business days.
                </p>
              </GlassPanel>

              {alert && (
                <div className="mb-4">
                  <Alert
                    type={alert.type}
                    icon={alert.type === "success" ? CheckCircle2 : AlertCircle}
                  >
                    {alert.message}
                  </Alert>
                </div>
              )}

              {isCreator && (
                <div className="mb-5">
                  <FilterTabs
                    value={filter}
                    onChange={setFilter}
                    tabs={[
                      { id: "all", label: "All", icon: LayoutGrid },
                      { id: "money", label: "Cash", icon: Wallet },
                      { id: "coupon", label: "Coupons", icon: Ticket },
                    ]}
                  />
                </div>
              )}

              {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonCard key={index} />
                  ))}
                </div>
              ) : availableRewards.length === 0 ? (
                <GlassPanel glow="purple">
                  <p className="text-sm text-white/60">
                    No rewards available right now. Check back soon.
                  </p>
                </GlassPanel>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {availableRewards.map((reward) => (
                    <RewardCard
                      key={reward.label}
                      reward={reward}
                      points={points}
                      submitting={submitting}
                      onRequest={() =>
                        handleRewardRequest(
                          reward.type,
                          reward.cost,
                          reward.label
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}

const GlassPanel = ({ children, glow = "purple", className = "" }) => {
    const color = glow === "mint" ? MINT : PURPLE;

    return (
        <div
            className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6 ${className}`}
            style={{
                boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05), 0 20px 60px -30px ${color}66`,
            }}
        >
            <div
                className="pointer-events-none absolute -right-20 -top-24 h-48 w-48 rounded-full opacity-30 blur-3xl"
                style={{ background: color }}
            />
            <div className="relative">{children}</div>
        </div>
    );
};

const Alert = ({ type = "success", icon: Icon = CheckCircle2, children }) => {
    const styles =
        type === "success"
            ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-100"
            : "border-red-400/20 bg-red-500/10 text-red-200";

    return (
        <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${styles}`}>
            <Icon size={18} className="mt-0.5 shrink-0" />
            <p>{children}</p>
        </div>
    );
};

const ProgressBar = ({ value = 0 }) => (
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
            className="h-full rounded-full transition-all"
            style={{
                width: `${Math.min(Math.max(value, 0), 100)}%`,
                background: `linear-gradient(90deg, ${MINT}, #34d399)`,
                boxShadow: `0 0 12px ${MINT}88`,
            }}
        />
    </div>
);

const FilterTabs = ({ tabs, value, onChange }) => (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-1 backdrop-blur-xl">
        <div className="flex gap-1">
            {tabs.map((tab) => {
                const active = value === tab.id;
                const Icon = tab.icon;

                return (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => onChange(tab.id)}
                        className={`relative flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition sm:text-sm ${active ? "text-white" : "text-white/50 hover:text-white/80"
                            }`}
                        style={
                            active
                                ? {
                                    background: "rgba(255,255,255,0.06)",
                                    boxShadow: `inset 0 0 0 1px rgba(16,185,129,0.25), 0 8px 24px -12px ${MINT}`,
                                }
                                : undefined
                        }
                    >
                        {Icon && <Icon size={14} style={{ color: active ? MINT : undefined }} />}
                        {tab.label}
                    </button>
                );
            })}
        </div>
    </div>
);

const RewardCard = ({ reward, points, submitting, onRequest }) => {
    const canAfford = points >= reward.cost;
    const short = reward.cost - points;
    const isCash = reward.type === "money";
    const TypeIcon = isCash ? Wallet : Ticket;

    return (
        <div
            className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/[0.05]"
            style={{
                boxShadow: `0 20px 60px -30px ${isCash ? MINT : PURPLE}66`,
            }}
        >
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-white/5">
                <img
                    src={reward.image}
                    alt={reward.label}
                    fetchPriority="high"
                    decoding="async"
                    loading="eager"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07060d] via-transparent to-transparent" />
                <div className="absolute left-3 top-3">
                    <span
                        className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md"
                        style={{
                            color: isCash ? MINT : PURPLE,
                            background: isCash
                                ? "rgba(16,185,129,0.15)"
                                : "rgba(168,85,247,0.15)",
                            borderColor: isCash
                                ? "rgba(16,185,129,0.3)"
                                : "rgba(168,85,247,0.3)",
                        }}
                    >
                        <TypeIcon size={11} />
                        {isCash ? "Cash" : "Coupon"}
                    </span>
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-4 p-4 sm:p-5">
                <div>
                    <h3 className="text-base font-bold text-white sm:text-lg">
                        {reward.label}
                    </h3>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-white/60">
                        <Coins size={14} style={{ color: MINT }} />
                        <span className="font-semibold text-white/80">{reward.cost}</span>
                        <span>points</span>
                        {!canAfford && (
                            <span className="ml-auto text-xs text-white/40">{short} short</span>
                        )}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onRequest}
                    disabled={!canAfford || submitting}
                    className={
                        canAfford
                            ? "mt-auto inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                            : "mt-auto inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white/40"
                    }
                    style={
                        canAfford
                            ? {
                                background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                                boxShadow: `0 16px 40px -12px ${MINT}, inset 0 0 0 1px rgba(255,255,255,0.1)`,
                            }
                            : undefined
                    }
                >
                    {submitting ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : canAfford ? (
                        <Gift size={14} />
                    ) : (
                        <Lock size={14} />
                    )}
                    {canAfford ? "Request" : "Not enough points"}
                </button>
            </div>
        </div>
    );
};

const SkeletonCard = () => (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl">
        <div className="aspect-[16/10] w-full animate-pulse bg-white/[0.04]" />
        <div className="space-y-3 p-5">
            <div className="h-4 w-2/3 animate-pulse rounded bg-white/[0.06]" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-white/[0.05]" />
            <div className="h-10 w-full animate-pulse rounded-2xl bg-white/[0.05]" />
        </div>
    </div>
);
